import { useState, useMemo } from "react";
import {
  Calculator,
  TrendingUp,
  Home as HomeIcon,
  Share2,
  RotateCcw,
} from "lucide-react";
import SEO from "../ui/SEO.jsx";
import {
  Card,
  Label,
  Helper,
  YenInput,
  SegmentedControl,
  RangeWithValue,
  CheckboxRow,
  NextButton,
  SecondaryButton,
  BackLink,
  SectionTitle,
  BreakdownRow,
  ProgressDots,
  Eyebrow,
  Disclaimer,
} from "../ui/primitives.jsx";
import { calculateTakeHome, projectFutureValue } from "../lib/tax.js";
import { jpyToBrlPurchasingPower } from "../lib/fx.js";
import { useFxRates } from "../lib/useFxRates.jsx";
import { yen, brl } from "../lib/format.js";
import AdSlot from "../ui/AdSlot.jsx";
import { AD_SLOTS } from "../lib/adsense.js";

export default function SalarioCalc() {
  const { rates } = useFxRates();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    monthlyGross: 250000,
    age: 35,
    insuranceType: "shakai",
    dependents: 0,
    isFirstYearResident: false,
    yearsMore: 5,
    savingsRate: 30,
    currentSavings: 0,
  });

  const result = useMemo(() => {
    const take = calculateTakeHome(form);
    const monthlySavings = take.monthlyNet * (form.savingsRate / 100);
    const totalJPY = projectFutureValue({
      monthlyContribution: monthlySavings,
      years: form.yearsMore,
      currentValue: form.currentSavings,
    });
    const spEquivalent = jpyToBrlPurchasingPower(take.monthlyNet, {
      jpyToBrl: rates.JPY_TO_BRL,
    });
    return {
      ...take,
      future: {
        monthlySavings,
        totalJPY,
        totalBRL: totalJPY * rates.JPY_TO_BRL,
      },
      spEquivalent,
    };
  }, [form, rates]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const totalSteps = 3;

  return (
    <div>
      <SEO
        title="Calculadora de salário líquido"
        description="Quanto você realmente ganha no Japão. Salário líquido após shakai hoken, imposto e residencial."
        path="/calculadoras/salario"
      />

      {step === 0 && <StepIntro form={form} update={update} onNext={() => setStep(1)} />}
      {step === 1 && <StepFamily form={form} update={update} onNext={() => setStep(2)} onBack={() => setStep(0)} />}
      {step === 2 && <StepFuture form={form} update={update} onNext={() => setStep(3)} onBack={() => setStep(1)} />}
      {step === 3 && <ResultScreen form={form} result={result} onRestart={() => setStep(0)} />}

      {step < 3 && <ProgressDots current={step} total={totalSteps} />}
    </div>
  );
}

// ---------- Steps ----------

function StepIntro({ form, update, onNext }) {
  return (
    <div>
      <Eyebrow>Calculadora honesta</Eyebrow>
      <h1 className="font-serif font-medium text-[40px] md:text-[56px] leading-[1.05] tracking-tight mb-5">
        Quanto você{" "}
        <span className="italic text-accent">realmente</span>{" "}
        ganha no Japão?
      </h1>
      <p className="text-base md:text-[17px] leading-relaxed text-ink-soft max-w-xl mb-10">
        Salário bruto não conta a história toda. Em 3 perguntas você vê o líquido,
        quanto isso compraria em São Paulo, e o que você teria guardado se
        ficar mais alguns anos.
      </p>

      <Card>
        <Label num="01">Seu salário bruto mensal</Label>
        <YenInput
          value={form.monthlyGross}
          onChange={(v) => update("monthlyGross", v)}
        />
        <Helper>
          Valor antes dos descontos. O que aparece no contrato ou no kyuyo
          meisai (給与明細) na linha de cima.
        </Helper>

        <div className="h-6" />

        <Label num="02">Tipo de seguro</Label>
        <SegmentedControl
          value={form.insuranceType}
          onChange={(v) => update("insuranceType", v)}
          options={[
            { value: "shakai", label: "Shakai Hoken", sub: "社会保険" },
            { value: "kokumin", label: "Kokumin Hoken", sub: "国民健康保険" },
          ]}
        />
        <Helper>
          Shakai = seguro da empresa (regular). Kokumin = autônomo, contrato
          parcial, ou quando você mesmo paga.
        </Helper>
      </Card>

      <NextButton onClick={onNext}>Próximo</NextButton>
    </div>
  );
}

function StepFamily({ form, update, onNext, onBack }) {
  return (
    <div>
      <BackLink onClick={onBack} />
      <h2 className="font-serif font-medium text-4xl leading-tight tracking-tight mb-8">
        Algumas perguntas sobre sua situação.
      </h2>

      <Card>
        <Label num="03">Sua idade</Label>
        <RangeWithValue
          min={18}
          max={70}
          value={form.age}
          onChange={(v) => update("age", v)}
          format={(v) => `${v} anos`}
        />
        <Helper>
          A partir dos 40 anos paga-se também kaigo hoken (介護保険).
        </Helper>

        <div className="h-6" />

        <Label num="04">Dependentes</Label>
        <RangeWithValue
          min={0}
          max={6}
          value={form.dependents}
          onChange={(v) => update("dependents", v)}
          format={(v) =>
            v === 0 ? "nenhum" : v === 1 ? "1 dependente" : `${v} dependentes`
          }
        />
        <Helper>
          Cônjuge sem renda própria e filhos a seu cargo reduzem o imposto.
        </Helper>

        <div className="h-6" />

        <CheckboxRow
          checked={form.isFirstYearResident}
          onChange={(v) => update("isFirstYearResident", v)}
          label="Estou no primeiro ano fiscal no Japão"
          helper="Se for verdade, o juushinkoku (住民税) será ¥0 este ano — ele é cobrado com base no ano anterior."
        />
      </Card>

      <NextButton onClick={onNext}>Próximo</NextButton>
    </div>
  );
}

function StepFuture({ form, update, onNext, onBack }) {
  return (
    <div>
      <BackLink onClick={onBack} />
      <h2 className="font-serif font-medium text-4xl leading-tight tracking-tight mb-8">
        E quanto ao{" "}
        <span className="italic text-accent">futuro</span>?
      </h2>

      <Card>
        <Label num="05">Quantos anos mais no Japão?</Label>
        <RangeWithValue
          min={1}
          max={20}
          value={form.yearsMore}
          onChange={(v) => update("yearsMore", v)}
          format={(v) => `${v} ${v === 1 ? "ano" : "anos"}`}
        />

        <div className="h-6" />

        <Label num="06">Quanto consegue poupar do líquido?</Label>
        <RangeWithValue
          min={0}
          max={70}
          value={form.savingsRate}
          onChange={(v) => update("savingsRate", v)}
          format={(v) => `${v}%`}
        />
        <Helper>
          Seja honesto. A média de quem está realmente guardando fica entre 25% e 40%.
        </Helper>

        <div className="h-6" />

        <Label num="07">Quanto já tem guardado? (opcional)</Label>
        <YenInput
          value={form.currentSavings}
          onChange={(v) => update("currentSavings", v)}
        />
      </Card>

      <NextButton onClick={onNext}>Ver resultado</NextButton>
    </div>
  );
}

// ---------- Result ----------

function ResultScreen({ form, result, onRestart }) {
  const shareText = useMemo(() => {
    return (
      `Aqui no Japão — meu cálculo:\n\n` +
      `💴 Líquido: ${yen(result.monthlyNet)}/mês\n` +
      `🇧🇷 Equivalente em SP: ${brl(result.spEquivalent)}/mês\n` +
      `📈 Em ${form.yearsMore} anos: ${brl(result.future.totalBRL)}\n\n` +
      `Faça o seu: aquinojapao.com/calculadoras/salario`
    );
  }, [result, form.yearsMore]);

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  return (
    <div>
      <Eyebrow>Seu resultado</Eyebrow>
      <h2 className="font-serif font-medium text-4xl leading-tight tracking-tight mb-8">
        Aqui está a foto{" "}
        <span className="italic">completa</span>.
      </h2>

      {/* Hero result */}
      <div className="bg-ink text-bg rounded-2xl p-8 mb-4 relative overflow-hidden">
        <div className="text-xs tracking-[0.18em] uppercase opacity-60 mb-2">
          Salário líquido mensal
        </div>
        <div className="font-serif text-[56px] md:text-[72px] leading-none tracking-tight font-medium">
          {yen(result.monthlyNet)}
        </div>
        <div className="mt-3 text-sm opacity-70 font-mono">
          De {yen(result.monthlyGross)} bruto · {result.effectiveRate.toFixed(1)}% em descontos
        </div>
        <div
          className="absolute top-6 right-6 border-2 border-accent text-accent px-3 py-1.5 rounded text-[10px] tracking-[0.2em] font-bold opacity-85"
          style={{ transform: "rotate(8deg)" }}
        >
          実質
        </div>
      </div>

      {/* Breakdown */}
      <Card className="mb-4">
        <SectionTitle icon={<Calculator size={16} />}>
          Para onde vai o dinheiro
        </SectionTitle>
        <BreakdownRow
          label={form.insuranceType === "shakai" ? "Shakai hoken" : "Kokumin hoken + nenkin"}
          value={yen(result.breakdown.socialInsurance)}
        />
        <BreakdownRow
          label="Imposto de renda (所得税)"
          value={yen(result.breakdown.incomeTax)}
        />
        <BreakdownRow
          label={form.isFirstYearResident ? "Imposto residencial (1º ano)" : "Imposto residencial (住民税)"}
          value={yen(result.breakdown.residentTax)}
        />
        <BreakdownRow
          label="Total descontado"
          value={yen(
            result.breakdown.socialInsurance +
              result.breakdown.incomeTax +
              result.breakdown.residentTax
          )}
          bold
        />
      </Card>

      {/* SP equivalent */}
      <Card variant="accent" className="mb-4">
        <SectionTitle icon={<HomeIcon size={16} />}>
          Equivalente em São Paulo
        </SectionTitle>
        <div className="font-serif text-[44px] leading-none font-medium tracking-tight text-ink mb-2">
          {brl(result.spEquivalent)}
          <span className="text-xl opacity-60">/mês</span>
        </div>
        <p className="text-sm text-ink-soft leading-relaxed">
          A renda em SP que daria o mesmo poder de compra do seu líquido japonês.
          Não inclui qualidade de serviços públicos, segurança, ou custo emocional
          da distância.
        </p>
      </Card>

      {/* Future */}
      <Card className="mb-6">
        <SectionTitle icon={<TrendingUp size={16} />}>
          Se você ficar mais {form.yearsMore} {form.yearsMore === 1 ? "ano" : "anos"}
        </SectionTitle>
        <div className="font-serif text-[44px] leading-none font-medium tracking-tight mb-1">
          {yen(result.future.totalJPY)}
        </div>
        <div className="font-mono text-base text-success mb-4">
          ≈ {brl(result.future.totalBRL)}
        </div>
        <p className="text-sm text-ink-soft leading-relaxed">
          Guardando {form.savingsRate}% do líquido todo mês (
          {yen(result.future.monthlySavings)}/mês), com retorno conservador
          de 3% ao ano. Não considera variação cambial — esse é o maior risco real.
        </p>
      </Card>

      {/* Actions */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2.5 bg-accent text-white py-4 px-6 rounded-xl font-semibold no-underline mb-3 hover:bg-accent/90 transition-colors"
      >
        <Share2 size={18} />
        Compartilhar no WhatsApp
      </a>

      <SecondaryButton onClick={onRestart}>
        <RotateCcw size={16} />
        Refazer o cálculo
      </SecondaryButton>

      <Disclaimer>
        <strong className="text-ink">Aviso:</strong> esta é uma estimativa.
        Taxas de shakai hoken variam por prefeitura. Kokumin kenko hoken varia
        muito por município (de ~7% a ~13% da renda). Para decisões importantes,
        consulte um contador (税理士) ou sua prefeitura.
      </Disclaimer>

      <AdSlot slot={AD_SLOTS.calcResult} format="auto" />
    </div>
  );
}
